import { ReactElement, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import cogoToast from "cogo-toast";
import { sendEmailCode } from "../api/LoginApi";

interface Enable2FAModalProps {
    show: boolean;
    userEmail: string;
    onEnable: (method: 'EMAIL' | 'TOTP', interval: number, code?: string) => Promise<void>;
    onSkip: () => void;
    onSetupTOTP?: () => void;
}

const Enable2FAModal = ({ show, userEmail, onEnable, onSkip, onSetupTOTP }: Enable2FAModalProps): ReactElement => {
    // Interval defaults to 0 (ignored implicitly now that it's time-based, but kept for interface compatibility if needed)
    const [interval] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');

    const handleInitialSubmit = async () => {
        setIsSubmitting(true);
        try {
            await sendEmailCode(userEmail);
            setVerifyingEmail(true);
            cogoToast.success('Código enviado a tu correo');
        } catch (error) {
            cogoToast.error('No se pudo enviar el código al correo');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinalEnable = async () => {
        if (verificationCode.length !== 6) {
            cogoToast.warn('Por favor ingresa un código de 6 dígitos');
            return;
        }
        setIsSubmitting(true);
        try {
            await onEnable('EMAIL', interval, verificationCode);
        } catch (error: any) {
            const msg = error.response?.data?.message || 'Código inválido o error al habilitar 2FA';
            cogoToast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onSkip}
            centered
            backdrop="static"
            size="lg"
            scrollable
            fullscreen="sm-down"
            className="enable-2fa-modal"
        >
            <Modal.Header className="border-0 pb-0" style={{
                background: 'linear-gradient(135deg, #00a854 0%, #008042 100%)',
                padding: '2.5rem 2rem 2rem 2rem',
                position: 'relative'
            }}>
                <Button
                    variant="link"
                    onClick={onSkip}
                    className="position-absolute top-0 end-0 text-white p-0 m-3"
                    style={{ opacity: 0.9, zIndex: 10 }}
                >
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>&times;</span>
                </Button>
                <div className="w-100 text-center">
                    <div className="mb-3">

                    </div>
                    <h2 className="text-white fw-bold mb-2" style={{ fontSize: '28px', letterSpacing: '-0.5px' }}>
                        Protege tu Cuenta
                    </h2>
                    <p className="text-white mb-0" style={{ opacity: 0.95, fontSize: '1rem', fontWeight: '400' }}>
                        Activa la autenticación de dos factores para mayor seguridad
                    </p>
                </div>
            </Modal.Header>

            <Modal.Body className="p-4 pb-3">
                <div className="mb-4">
                    <h6 className="fw-semibold mb-3" style={{ color: '#2c3e50', fontSize: '0.85rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        Método de Autenticación
                    </h6>

                    <div
                        className="email-card p-4"
                        style={{
                            border: '2px solid #008042',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(0, 128, 66, 0.03) 0%, rgba(0, 168, 84, 0.06) 100%)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="d-flex align-items-start">
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                    <h5 className="mb-0 fw-bold" style={{ color: '#1a1a1a', fontSize: '1.1rem' }}>
                                        Correo Electrónico
                                    </h5>
                                    <span style={{
                                        fontSize: '0.7rem',
                                        background: '#008042',
                                        color: 'white',
                                        padding: '3px 10px',
                                        borderRadius: '6px',
                                        fontWeight: '700',
                                        letterSpacing: '0.3px'
                                    }}>PRINCIPAL</span>
                                </div>
                                <p className="mb-3" style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                                    Recibirás un código de seguridad de 6 dígitos en tu correo registrado.
                                </p>
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <div className="d-flex gap-2 flex-wrap text-muted fw-semibold" style={{ fontSize: '0.85rem' }}>
                                        ✓ Fácil de usar
                                        ✓ No requiere apps de terceros
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {verifyingEmail && (
                    <div className="mb-4 animate__animated animate__fadeIn">
                        <label className="fw-bold mb-2">Ingresa el código que enviamos a {userEmail}</label>
                        <input
                            type="text"
                            className="form-control text-center py-3 fs-4"
                            maxLength={6}
                            style={{ letterSpacing: '0.5em', fontWeight: 'bold' }}
                            placeholder="000000"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        />
                    </div>
                )}

            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 pb-4 px-4">
                <div className="w-100">
                    {!verifyingEmail ? (
                        <Button
                            onClick={handleInitialSubmit}
                            className="w-100 mb-3"
                            style={{
                                background: 'linear-gradient(135deg, #008042 0%, #00a854 100%)',
                                border: 'none',
                                padding: '14px',
                                borderRadius: '12px',
                                fontWeight: '700',
                                fontSize: '16px',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(0, 128, 66, 0.25)'
                            }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Activar 2FA con Correo'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleFinalEnable}
                            className="w-100 mb-3"
                            style={{
                                background: 'linear-gradient(135deg, #008042 0%, #00a854 100%)',
                                border: 'none',
                                padding: '14px',
                                borderRadius: '12px',
                                fontWeight: '700',
                                fontSize: '16px',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(0, 128, 66, 0.25)'
                            }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Confirmar Código y Activar'}
                        </Button>
                    )}

                    <Button
                        variant="outline-secondary"
                        onClick={onSetupTOTP}
                        className="w-100 mb-3"
                        style={{
                            borderRadius: '12px',
                            padding: '12px',
                            fontWeight: '600',
                            fontSize: '15px',
                        }}
                    >
                        Configurar con App Autenticadora (Avanzado)
                    </Button>

                    <Button
                        variant="link"
                        onClick={onSkip}
                        className="w-100 text-muted"
                        style={{
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                        }}
                    >
                        Recordarme más tarde
                    </Button>
                </div>
            </Modal.Footer>

            <style>{`
                .totp-card {
                    transition: all 0.3s ease;
                }
                
                .interval-option:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0, 128, 66, 0.15);
                }
                
                .enable-2fa-modal .modal-content {
                    border-radius: 20px;
                    overflow: hidden;
                    border: none;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }
                
                .enable-2fa-modal .btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0, 128, 66, 0.35) !important;
                }
                
                .enable-2fa-modal .btn:active {
                    transform: translateY(0);
                }
            `}</style>
        </Modal>
    );
};

export default Enable2FAModal;
